import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  DollarSign, 
  Layers, 
  ChevronDown, 
  ChevronRight,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Edit3,
  Save,
  X,
  RefreshCw,
  MessageSquare,
  FileEdit
} from 'lucide-react';
import useAppStore from '../store/appStore';
import { generateMockProvisions, generateMockCBA, analyzeProvisions, generateCBA as generateCBAAPI, rewriteRegulation, consolidateRegulations, generateBulkCBA } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import ConsolidatedEditor from './ConsolidatedEditor';
import CBAGenerationAnimation from './CBAGenerationAnimation';
import CBADisplay from './CBADisplay';
import CBAComprehensiveDisplay from './CBAComprehensiveDisplay';
import RewriteDisplay from './RewriteDisplay';
import RewriteGenerationAnimation from './RewriteGenerationAnimation';
import VersionHistory from './VersionHistory';
import PastReports from './PastReports';
import WarningModal from './WarningModal';
import { getAgencyConfig } from '../config/agency';
import '../styles/rewrite.css';

const AnalysisPanel = ({ regulation, regulations }) => {
  const { viewMode, setViewMode, consolidatedDraft, isConsolidating, provisionsAnalysis } = useAppStore();
  const [provisions, setProvisions] = useState([]);
  const [cbaData, setCbaData] = useState(null);
  const [expandedProvisions, setExpandedProvisions] = useState([]);
  const [activeTab, setActiveTab] = useState('provisions');
  const [isLoadingProvisions, setIsLoadingProvisions] = useState(false);
  const [isLoadingCBA, setIsLoadingCBA] = useState(false);
  const [useRealAI, setUseRealAI] = useState(false); // Start with mock data
  const [cbaUserContext, setCbaUserContext] = useState('');
  const [showCBAInput, setShowCBAInput] = useState(false);
  const [cbaGenerated, setCbaGenerated] = useState(false);
  const [expandedCosts, setExpandedCosts] = useState([]);
  const [expandedBenefits, setExpandedBenefits] = useState([]);
  const [rewriteData, setRewriteData] = useState(null);
  const [isLoadingRewrite, setIsLoadingRewrite] = useState(false);
  const [rewriteError, setRewriteError] = useState(null);
  
  // Bulk operation states
  const [bulkCBAData, setBulkCBAData] = useState(null);
  const [bulkCBAGenerated, setBulkCBAGenerated] = useState(false);
  const [isLoadingBulkCBA, setIsLoadingBulkCBA] = useState(false);
  const [bulkCBAError, setBulkCBAError] = useState(null);
  const [consolidationData, setConsolidationData] = useState(null);
  const [isLoadingConsolidation, setIsLoadingConsolidation] = useState(false);
  const [consolidationError, setConsolidationError] = useState(null);
  
  // Version history states
  const [cbaHistory, setCbaHistory] = useState([]);
  const [rewriteHistory, setRewriteHistory] = useState([]);
  const [bulkCBAHistory, setBulkCBAHistory] = useState([]);
  const [consolidationHistory, setConsolidationHistory] = useState([]);
  const [currentCBAVersion, setCurrentCBAVersion] = useState(0);
  const [currentRewriteVersion, setCurrentRewriteVersion] = useState(0);
  const [currentBulkCBAVersion, setCurrentBulkCBAVersion] = useState(0);
  const [currentConsolidationVersion, setCurrentConsolidationVersion] = useState(0);
  
  // Refine states for bulk operations
  const [showBulkCBARefine, setShowBulkCBARefine] = useState(false);
  const [bulkCBARefineContext, setBulkCBARefineContext] = useState('');
  const [showConsolidationRefine, setShowConsolidationRefine] = useState(false);
  const [consolidationRefineContext, setConsolidationRefineContext] = useState('');
  
  // View mode toggle for Live/Past Reports
  const [analysisView, setAnalysisView] = useState('live'); // 'live' or 'past'
  
  // Warning modal state for consolidation only
  const [showConsolidationWarning, setShowConsolidationWarning] = useState(false);
  const [consolidationWarningStatutes, setConsolidationWarningStatutes] = useState([]);
  const [pendingConsolidationContext, setPendingConsolidationContext] = useState(null);
  
  // Check if we're on an agency-specific page
  const agencyConfig = getAgencyConfig();
  const consolidationMode = useAppStore(state => state.consolidationMode);
  // Disable CBA features completely
  const showCBAFeatures = false;

  useEffect(() => {
    if (regulation) {
      // Clear analysis data when regulation changes
      setCbaData(null);
      setCbaGenerated(false);
      setCbaUserContext('');
      setShowCBAInput(false);
      setRewriteData(null);
      setRewriteError(null);
      
      // Load fresh provisions for new regulation
      loadProvisions();
      
      console.log('%c🔄 [AnalysisPanel] Regulation changed - cleared analysis', 'color: #F59E0B');
    }
  }, [regulation]);

  // Auto-adjust tabs when switching between single and multiple selection
  useEffect(() => {
    const isMulti = regulations && regulations.length > 1;
    
    // If switching from multi to single selection
    if (!isMulti && (activeTab === 'bulk-cba' || activeTab === 'consolidate' || viewMode === 'bulk-cba' || viewMode === 'consolidate')) {
      setActiveTab('provisions');
      setViewMode('provisions');  // Also update viewMode
    }
    // If switching from single to multi selection
    else if (isMulti && (activeTab === 'provisions' || activeTab === 'cba' || activeTab === 'rewrite')) {
      // Check if we're on an agency-specific page
      const agencyConfig = window.location.pathname.match(/\/(treasury|doe|vdh|dmme|deq|dcjs|dbhds|dss|vdacs|dmas|dpor)\/?$/i);
      if (agencyConfig) {
        // On agency pages, go straight to consolidate tab (no bulk CBA)
        setActiveTab('consolidate');
        setViewMode('consolidate');
      } else {
        // On main page, default to bulk-cba
        setActiveTab('bulk-cba');
        setViewMode('bulk-cba');
      }
    }
    
    // Clear ALL analysis data when selection changes
    // For single regulation views
    setCbaData(null);
    setCbaGenerated(false);
    setCbaUserContext('');
    setShowCBAInput(false);
    setRewriteData(null);
    setRewriteError(null);
    // Don't clear provisions - they come from the store
    
    // For multi-regulation views
    setBulkCBAData(null);
    setBulkCBAGenerated(false);
    setConsolidationData(null);
    setShowBulkCBARefine(false);
    setBulkCBARefineContext('');
    setShowConsolidationRefine(false);
    setConsolidationRefineContext('');
    
    console.log('%c🔄 [AnalysisPanel] Selection changed - cleared all analysis data', 'color: #F59E0B');
  }, [regulations]);

  // Reset multi-regulation data when selection changes (not just count)
  useEffect(() => {
    if (regulations && regulations.length > 1) {
      // Create a unique key for the current selection
      const selectionKey = regulations.map(r => r.id).sort().join(',');
      
      // Check if this is a different selection than before
      if (window.lastMultiSelectionKey && window.lastMultiSelectionKey !== selectionKey) {
        // Reset all multi-reg data when selection changes
        setBulkCBAData(null);
        setBulkCBAGenerated(false);
        setConsolidationData(null);
        setIsLoadingBulkCBA(false);
        setIsLoadingConsolidation(false);
        setBulkCBAError(null);
        setConsolidationError(null);
        setBulkCBAHistory([]);
        setConsolidationHistory([]);
        setCurrentBulkCBAVersion(0);
        setCurrentConsolidationVersion(0);
      }
      
      // Store the current selection key
      window.lastMultiSelectionKey = selectionKey;
    }
  }, [regulations]);

  const loadProvisions = async () => {
    if (!regulation) return;
    
    setIsLoadingProvisions(true);
    try {
      // First try to use real provisions from the store
      const realProvisions = provisionsAnalysis[regulation.id];
      if (realProvisions && realProvisions.length > 0) {
        setProvisions(realProvisions);
      } else if (useRealAI && regulation.text) {
        // Make real AI call
        const result = await analyzeProvisions(regulation.id, {
          text: regulation.text,
          regulation_text: regulation.text
        });
        setProvisions(Array.isArray(result) ? result : result.provisions || []);
      } else {
        // Use mock data only as last resort
        setProvisions(generateMockProvisions(regulation.id));
      }
    } catch (error) {
      console.error('Failed to load provisions:', error);
      // Try to use real provisions from store before falling back to mock
      const realProvisions = provisionsAnalysis[regulation.id];
      if (realProvisions && realProvisions.length > 0) {
        setProvisions(realProvisions);
      } else {
        setProvisions(generateMockProvisions(regulation.id));
      }
    } finally {
      setIsLoadingProvisions(false);
    }
  };

  const loadCBA = async (includeUserContext = false, contextOverride = null) => {
    console.log('%c🔄 [AnalysisPanel] loadCBA called', 'color: #F59E0B; font-weight: bold', {
      includeUserContext,
      contextOverride,
      hasRegulation: !!regulation
    });
    
    if (!regulation) {
      console.error('%c❌ [AnalysisPanel] No regulation selected!', 'color: #EF4444; font-weight: bold');
      return;
    }
    
    const contextToUse = contextOverride || cbaUserContext;
    console.log('%c📝 [AnalysisPanel] Context to use:', 'color: #3B82F6', contextToUse);
    
    setIsLoadingCBA(true);
    console.log('%c💰 [AnalysisPanel] Starting CBA generation...', 'color: #10B981; font-weight: bold', {
      regulationId: regulation.id,
      regulationTitle: regulation.title,
      hasUserContext: includeUserContext && contextToUse.trim().length > 0,
      contextLength: contextToUse?.length || 0
    });
    
    try {
      console.log('%c🌐 [AnalysisPanel] Calling API...', 'color: #6366F1');
      const apiPayload = {
        regulation_text: regulation.text,
        user_context: includeUserContext && contextToUse.trim() ? contextToUse : null
      };
      console.log('%c📦 [AnalysisPanel] API Payload:', 'color: #6366F1', apiPayload);
      
      const result = await generateCBAAPI(regulation.id, apiPayload);
      console.log('%c✅ [AnalysisPanel] CBA API Response:', 'color: #10B981; font-weight: bold', result);
      
      // Extract the CBA data from the response
      const cbaData = result.cba || result;
      setCbaData(cbaData);
      setCbaGenerated(true);
      // Add to history with regulation metadata
      const newHistory = [...cbaHistory, { 
        data: cbaData, 
        context: contextToUse, 
        timestamp: new Date(),
        regulationTitle: regulation?.title || 'Unknown Regulation',
        regulationNumber: regulation?.number || regulation?.id || 'Unknown'
      }];
      setCbaHistory(newHistory);
      setCurrentCBAVersion(newHistory.length - 1);
      
      // Clear input after successful generation with context
      if (includeUserContext) {
        setCbaUserContext('');
        setShowCBAInput(false);
      }
    } catch (error) {
      console.error('Failed to load CBA:', error);
      // NO FALLBACKS - SHOW THE ERROR
      alert(`CBA Generation Failed: ${error.message || error}`);
      setCbaData(null);
      setCbaGenerated(false);
    } finally {
      setIsLoadingCBA(false);
    }
  };

  const handleRegenerateCBA = () => {
    console.log('%c🔄 [AnalysisPanel] Regenerating CBA with context', 'color: #F59E0B');
    loadCBA(true);
  };

  const loadRewrite = async (userContext = null) => {
    if (!regulation) return;
    
    setIsLoadingRewrite(true);
    setRewriteError(null);
    console.log('%c📝 [AnalysisPanel] Generating plain language rewrite...', 'color: #10B981', {
      regulationId: regulation.id,
      hasContext: !!userContext,
      hasPreGeneratedRewrite: !!regulation.rewritten_text
    });
    
    try {
      // Check if we have a pre-generated rewrite
      if (regulation.rewritten_text) {
        // Add artificial delay for better UX
        console.log('%c⏱️ [AnalysisPanel] Using pre-generated rewrite with delay...', 'color: #10B981');
        await new Promise(resolve => setTimeout(resolve, 2500)); // 2.5 second delay
        
        // Create result object matching the expected format
        const result = {
          original_text: regulation.text,
          rewritten_text: regulation.rewritten_text,
          rewrite_analysis: regulation.rewrite_analysis || '', // Include the analysis field from pre-cached data
          changes_made: [
            "Eliminated Supreme Court violations",
            "Simplified language for clarity",
            "Aligned with statutory authority",
            "Removed unauthorized provisions"
          ],
          compliance_score: 95,
          readability_improvement: "+25%"
        };
        
        console.log('%c✅ [AnalysisPanel] Rewrite loaded successfully', 'color: #10B981', result);
        setRewriteData(result);
        
        // Add to history with regulation metadata
        const newHistory = [...rewriteHistory, { 
          data: result, 
          context: userContext, 
          timestamp: new Date(),
          regulationTitle: regulation?.title || 'Unknown Regulation',
          regulationNumber: regulation?.number || regulation?.id || 'Unknown'
        }];
        setRewriteHistory(newHistory);
        setCurrentRewriteVersion(newHistory.length - 1);
      } else {
        // Fallback to API call if no pre-generated rewrite
        const result = await rewriteRegulation(regulation.text, provisions, userContext);
        console.log('%c✅ [AnalysisPanel] Rewrite generated via API', 'color: #10B981', result);
        setRewriteData(result);
        
        // Add to history with regulation metadata
        const newHistory = [...rewriteHistory, { 
          data: result, 
          context: userContext, 
          timestamp: new Date(),
          regulationTitle: regulation?.title || 'Unknown Regulation',
          regulationNumber: regulation?.number || regulation?.id || 'Unknown'
        }];
        setRewriteHistory(newHistory);
        setCurrentRewriteVersion(newHistory.length - 1);
      }
    } catch (error) {
      console.error('Failed to generate rewrite:', error);
      setRewriteError(error.message || 'Failed to generate rewrite');
    } finally {
      setIsLoadingRewrite(false);
    }
  };

  const handleRefineRewrite = (context) => {
    console.log('%c🔄 [AnalysisPanel] Refining rewrite with context', 'color: #F59E0B', context);
    loadRewrite(context);
  };
  
  // Bulk CBA functions
  const loadBulkCBA = async (userContext = null) => {
    if (!regulations || regulations.length < 2) return;
    
    setIsLoadingBulkCBA(true);
    setBulkCBAError(null);
    console.log('%c💰 [AnalysisPanel] Generating bulk CBA...', 'color: #10B981', {
      regulationCount: regulations.length,
      hasContext: !!userContext
    });
    
    try {
      const result = await generateBulkCBA(regulations, userContext);
      console.log('%c✅ [AnalysisPanel] Bulk CBA generated successfully', 'color: #10B981', result);
      setBulkCBAData(result);
      // Add to history with regulation metadata
      const newHistory = [...bulkCBAHistory, { 
        data: result, 
        context: userContext, 
        timestamp: new Date(),
        regulationCount: regulations?.length || 0
      }];
      setBulkCBAHistory(newHistory);
      setCurrentBulkCBAVersion(newHistory.length - 1);
    } catch (error) {
      console.error('Failed to generate bulk CBA:', error);
      setBulkCBAError(error.message || 'Failed to generate bulk CBA');
    } finally {
      setIsLoadingBulkCBA(false);
    }
  };
  
  // Consolidation functions
  const loadConsolidation = async (userContext = null) => {
    if (!regulations || regulations.length < 2) return;
    
    // Check for different authorizing statutes
    const uniqueStatutes = new Set(regulations.map(r => r.authorizingStatute).filter(Boolean));
    if (uniqueStatutes.size > 1) {
      // Show warning modal
      setConsolidationWarningStatutes(Array.from(uniqueStatutes));
      setPendingConsolidationContext(userContext);
      setShowConsolidationWarning(true);
      return;
    }
    
    // Proceed with consolidation if no warning needed
    await proceedWithConsolidation(userContext);
  };
  
  const proceedWithConsolidation = async (userContext = null) => {
    setShowConsolidationWarning(false);
    setIsLoadingConsolidation(true);
    setConsolidationError(null);
    
    const uniqueStatutes = new Set(regulations.map(r => r.authorizingStatute).filter(Boolean));
    console.log('%c📚 [AnalysisPanel] Consolidating regulations...', 'color: #10B981', {
      regulationCount: regulations.length,
      hasContext: !!userContext,
      uniqueStatutes: uniqueStatutes.size
    });
    
    try {
      const result = await consolidateRegulations(regulations, userContext);
      console.log('%c✅ [AnalysisPanel] Consolidation successful', 'color: #10B981', result);
      setConsolidationData(result);
      // Add to history
      const newHistory = [...consolidationHistory, { data: result, context: userContext, timestamp: new Date() }];
      setConsolidationHistory(newHistory);
      setCurrentConsolidationVersion(newHistory.length - 1);
    } catch (error) {
      console.error('Failed to consolidate regulations:', error);
      setConsolidationError(error.message || 'Failed to consolidate regulations');
    } finally {
      setIsLoadingConsolidation(false);
    }
  };

  const toggleProvision = (provisionId) => {
    setExpandedProvisions(prev =>
      prev.includes(provisionId)
        ? prev.filter(id => id !== provisionId)
        : [...prev, provisionId]
    );
  };

  // Determine if we're in multi-selection mode
  const isMultiSelection = regulations && regulations.length > 1;
  
  // Ensure activeTab is valid for current selection mode
  const validTab = isMultiSelection ? 
    (showCBAFeatures && activeTab === 'bulk-cba' ? activeTab : 
     activeTab === 'consolidate' ? activeTab : 
     showCBAFeatures ? 'bulk-cba' : 'consolidate') :
    (activeTab === 'provisions' || 
     (showCBAFeatures && activeTab === 'cba') || 
     activeTab === 'rewrite' ? activeTab : 'provisions');
  
  // Build tabs based on context
  const tabs = isMultiSelection ? [
    ...(showCBAFeatures ? [{ id: 'bulk-cba', label: 'Bulk Cost-Benefit Analysis', icon: DollarSign }] : []),
    { id: 'consolidate', label: 'Consolidate Regulations', icon: Layers },
  ] : [
    { id: 'provisions', label: 'Provisions Analysis', icon: FileText },
    ...(showCBAFeatures ? [{ id: 'cba', label: 'Cost-Benefit Analysis', icon: DollarSign }] : []),
    { id: 'rewrite', label: 'Plain Language Rewrite', icon: FileEdit },
  ];
  
  console.log('%c📑 [AnalysisPanel] Tabs configured:', 'color: #6366F1; font-weight: bold', tabs.map(t => t.id));
  console.log('%c🎯 [AnalysisPanel] Current viewMode:', 'color: #F59E0B', viewMode);

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Warning Modal for Consolidation */}
      <WarningModal
        isOpen={showConsolidationWarning}
        statutes={consolidationWarningStatutes}
        onClose={() => {
          setShowConsolidationWarning(false);
          setPendingConsolidationContext(null);
        }}
        onConfirm={() => {
          proceedWithConsolidation(pendingConsolidationContext);
        }}
      />
      {/* Live Analysis / Past Reports Toggle */}
      <div style={{
        padding: '12px 24px',
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{
          display: 'flex',
          background: '#f3f4f6',
          borderRadius: '8px',
          padding: '2px'
        }}>
          <button
            onClick={() => setAnalysisView('live')}
            style={{
              padding: '8px 16px',
              background: analysisView === 'live' ? 'white' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: analysisView === 'live' ? '#111827' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: analysisView === 'live' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
            }}
          >
            Live Analysis
          </button>
          <button
            onClick={() => setAnalysisView('past')}
            style={{
              padding: '8px 16px',
              background: analysisView === 'past' ? 'white' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: analysisView === 'past' ? '#111827' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: analysisView === 'past' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
            }}
          >
            Past Reports
          </button>
        </div>
        {analysisView === 'past' && (
          <div style={{
            fontSize: '13px',
            color: '#6b7280'
          }}>
            {showCBAFeatures ? 
              (cbaHistory.length + rewriteHistory.length + bulkCBAHistory.length + consolidationHistory.length) :
              (rewriteHistory.length + consolidationHistory.length)} total reports
          </div>
        )}
      </div>
      
      {/* Show Past Reports or Live Analysis */}
      {analysisView === 'past' ? (
        <PastReports
          cbaHistory={showCBAFeatures ? cbaHistory : []}
          rewriteHistory={rewriteHistory}
          bulkCBAHistory={showCBAFeatures ? bulkCBAHistory : []}
          consolidationHistory={consolidationHistory}
          onSelectReport={(report) => {
            // Switch back to live view and show the selected report
            setAnalysisView('live');
            
            // Set the appropriate tab and data based on report type
            if (report.type === 'cba') {
              setActiveTab('cba');
              setCbaData(report.data);
            } else if (report.type === 'rewrite') {
              setActiveTab('rewrite');
              setRewriteData(report.data);
            } else if (report.type === 'bulk-cba') {
              setActiveTab('bulk-cba');
              setBulkCBAData(report.data);
            } else if (report.type === 'consolidation') {
              setActiveTab('consolidate');
              setConsolidationData(report.data);
            }
          }}
        />
      ) : (
      <>
      {/* Tab Navigation - Modern Design */}
      <div style={{
        background: 'linear-gradient(to bottom, #ffffff, #f9fafb)',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 24px'
      }}>
        <div style={{
          display: 'flex',
          gap: '8px',
          paddingTop: '12px'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                console.log(`%c🖱️ [AnalysisPanel] Tab clicked: ${tab.id}`, 'color: #F59E0B; font-weight: bold', {
                  label: tab.label,
                  disabled: tab.disabled,
                  currentViewMode: viewMode
                });
                if (!tab.disabled) {
                  console.log(`%c🔄 [AnalysisPanel] Switching viewMode from "${viewMode}" to "${tab.id}"`, 'color: #3B82F6; font-weight: bold');
                  setViewMode(tab.id);
                } else {
                  console.warn(`%c⚠️ [AnalysisPanel] Tab "${tab.id}" is disabled`, 'color: #F59E0B');
                }
              }}
              disabled={tab.disabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: viewMode === tab.id
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'white',
                color: viewMode === tab.id ? 'white' : '#6b7280',
                border: viewMode === tab.id
                  ? 'none'
                  : '1px solid #e5e7eb',
                borderRadius: '8px 8px 0 0',
                fontSize: '14px',
                fontWeight: '600',
                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                opacity: tab.disabled ? 0.5 : 1,
                transition: 'all 0.3s ease',
                marginBottom: '-1px',
                position: 'relative',
                boxShadow: viewMode === tab.id
                  ? '0 -2px 8px rgba(102, 126, 234, 0.2)'
                  : 'none'
              }}
              onMouseEnter={(e) => {
                if (!tab.disabled && viewMode !== tab.id) {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!tab.disabled && viewMode !== tab.id) {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <tab.icon style={{ width: '16px', height: '16px' }} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <AnimatePresence mode="wait">
          {viewMode === 'provisions' && (
            <motion.div
              key="provisions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >

              {provisions.map((provision, index) => {
                // Determine background color based on three-bucket authorization level
                const getCardBackground = () => {
                  // Support both old and new classification systems
                  const authLevel = provision.authLevel || provision.authorization_level;
                  switch (authLevel) {
                    // New three-bucket system
                    case 'MANDATORY':
                    case 'Mandatory':
                      return 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)'; // Green
                    case 'AUTHORIZED':
                    case 'Authorized':
                      return 'linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%)'; // Yellow
                    case 'UNAUTHORIZED':
                    case 'Unauthorized':
                      return 'linear-gradient(135deg, #ffeeee 0%, #ffcdd2 100%)'; // Red
                    // Legacy four-bucket system
                    case 'Express Delegation':
                      return 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)';
                    case 'General Delegation':
                      return 'linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%)';
                    case 'Potential Overreach':
                      return 'linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%)';
                    default:
                      return 'linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%)';
                  }
                };

                const getAuthLabel = () => {
                  // Support both old and new classification systems
                  const authLevel = provision.authLevel || provision.authorization_level;
                  switch (authLevel) {
                    // New three-bucket system
                    case 'MANDATORY':
                    case 'Mandatory':
                      return { text: 'Mandatory', color: '#10b981', icon: CheckCircle }; // Green
                    case 'AUTHORIZED':
                    case 'Authorized':
                      return { text: 'Authorized', color: '#f59e0b', icon: AlertCircle }; // Yellow
                    case 'UNAUTHORIZED':
                    case 'Unauthorized':
                      return { text: 'Unauthorized', color: '#ef4444', icon: X }; // Red
                    // Legacy four-bucket system
                    case 'Express Delegation':
                      return { text: 'Express', color: '#155724', icon: CheckCircle };
                    case 'General Delegation':
                      return { text: 'General/Partial', color: '#856404', icon: AlertCircle };
                    case 'Potential Overreach':
                      return { text: 'Potential Overreach', color: '#6a1b9a', icon: AlertCircle };
                    default:
                      return { text: 'Authorized', color: '#f59e0b', icon: AlertCircle };
                  }
                };

                const authInfo = getAuthLabel();
                const AuthIcon = authInfo.icon;

                return (
                  <div
                    key={provision.id}
                    id={`provision-${regulation.id}-${index}`}
                    style={{
                      background: getCardBackground(),
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '12px',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      boxShadow: expandedProvisions.includes(provision.id)
                        ? '0 4px 12px rgba(0, 0, 0, 0.1)'
                        : '0 2px 4px rgba(0, 0, 0, 0.05)'
                    }}
                    onClick={() => toggleProvision(provision.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = expandedProvisions.includes(provision.id)
                        ? '0 4px 12px rgba(0, 0, 0, 0.1)'
                        : '0 2px 4px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <span style={{
                            background: 'white',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#374151',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                          }}>
                            Provision {index + 1}
                          </span>
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'rgba(255, 255, 255, 0.8)',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: authInfo.color
                          }}>
                            <AuthIcon style={{ width: '12px', height: '12px' }} />
                            {authInfo.text}
                          </span>
                          {/* Discretionary Requirements Badge */}
                          {provision.discretionary_requirement_count > 0 && (
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: '#dc2626'
                            }}>
                              {provision.discretionary_requirement_count} Disc. Req.
                            </span>
                          )}
                          {/* SCOTUS Violation Badge */}
                          {(provision.loper_bright_violation || provision.sackett_violation || provision.west_virginia_violation) && (
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: 'linear-gradient(135deg, #f3e8ff, #e9d5ff)',
                              border: '1px solid rgba(147, 51, 234, 0.2)',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: '#7c3aed'
                            }}>
                              Potential Violation
                            </span>
                          )}
                        </div>
                        <p style={{
                          fontSize: '14px',
                          lineHeight: '1.6',
                          color: '#1f2937',
                          fontFamily: 'Inter, sans-serif',
                          display: '-webkit-box',
                          WebkitLineClamp: expandedProvisions.includes(provision.id) ? 'unset' : 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: expandedProvisions.includes(provision.id) ? 'visible' : 'hidden'
                        }}>
                          {provision.text}
                        </p>
                      </div>
                      <div style={{ marginLeft: '16px' }}>
                        {expandedProvisions.includes(provision.id) ? (
                          <ChevronDown style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                        ) : (
                          <ChevronRight style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                        )}
                      </div>
                    </div>

                    <AnimatePresence>
                      {expandedProvisions.includes(provision.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          style={{
                            marginTop: '16px',
                            paddingTop: '16px',
                            borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {provision.authorizing_statute && (
                              <div>
                                <h5 style={{
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  marginBottom: '4px'
                                }}>
                                  Authorizing Statute
                                </h5>
                                <p style={{
                                  fontSize: '13px',
                                  color: '#2563eb',
                                  fontWeight: '500'
                                }}>
                                  {provision.authorizing_statute}
                                </p>
                                {provision.matched_statute_text && (
                                  <div style={{
                                    marginTop: '8px',
                                    padding: '10px 12px',
                                    background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05), rgba(79, 70, 229, 0.05))',
                                    borderLeft: '3px solid #2563eb',
                                    borderRadius: '4px'
                                  }}>
                                    <p style={{
                                      fontSize: '12px',
                                      lineHeight: '1.6',
                                      color: '#1e40af',
                                      fontStyle: 'italic',
                                      margin: 0
                                    }}>
                                      "{provision.matched_statute_text}"
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {provision.analysis && (
                              <div>
                                <h5 style={{
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  marginBottom: '4px'
                                }}>
                                  Analysis
                                </h5>
                                <p style={{
                                  fontSize: '13px',
                                  lineHeight: '1.6',
                                  color: '#374151',
                                  background: 'rgba(255, 255, 255, 0.6)',
                                  padding: '8px 12px',
                                  borderRadius: '6px'
                                }}>
                                  {provision.analysis}
                                </p>
                              </div>
                            )}
                            
                            {/* Display Discretionary Requirements */}
                            {provision.discretionary_requirement_count > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <h5 style={{
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  color: '#dc2626',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  marginBottom: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  Discretionary Requirements ({provision.discretionary_requirement_count})
                                </h5>
                                {provision.discretionary_requirements && provision.discretionary_requirements.length > 0 && (
                                  <div style={{
                                    background: 'rgba(254, 226, 226, 0.5)',
                                    border: '1px solid rgba(239, 68, 68, 0.2)',
                                    borderRadius: '6px',
                                    padding: '10px'
                                  }}>
                                    {provision.discretionary_requirements.map((req, idx) => (
                                      <div key={idx} style={{
                                        marginBottom: idx < provision.discretionary_requirements.length - 1 ? '8px' : 0,
                                        paddingBottom: idx < provision.discretionary_requirements.length - 1 ? '8px' : 0,
                                        borderBottom: idx < provision.discretionary_requirements.length - 1 ? '1px solid rgba(239, 68, 68, 0.1)' : 'none'
                                      }}>
                                        <div style={{
                                          fontSize: '12px',
                                          color: '#991b1b',
                                          fontWeight: '600',
                                          marginBottom: '4px'
                                        }}>
                                          {req.type === 'obligation' ? 'Obligation' : 
                                           req.type === 'prohibition' ? 'Prohibition' :
                                           req.type === 'condition' ? 'Condition' :
                                           'Standard'}: {req.affects}
                                        </div>
                                        <div style={{
                                          fontSize: '12px',
                                          color: '#7f1d1d',
                                          lineHeight: '1.5',
                                          fontStyle: 'italic'
                                        }}>
                                          "{req.text}"
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {provision.discretionary_analysis && (
                                  <p style={{
                                    fontSize: '12px',
                                    lineHeight: '1.5',
                                    color: '#7f1d1d',
                                    marginTop: '6px',
                                    fontStyle: 'italic'
                                  }}>
                                    {provision.discretionary_analysis}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* SCOTUS Violations Section */}
                            {(provision.loper_bright_violation || provision.sackett_violation || provision.west_virginia_violation) && (
                              <div style={{
                                background: 'rgba(243, 232, 255, 0.5)',
                                border: '1px solid rgba(147, 51, 234, 0.1)',
                                borderRadius: '8px',
                                padding: '12px'
                              }}>
                                <h4 style={{
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: '#7c3aed',
                                  marginBottom: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  Potential Supreme Court Violations
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                  {provision.loper_bright_violation && (
                                    <div>
                                      <h5 style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#6b21a8',
                                        marginBottom: '4px'
                                      }}>
                                        Loper Bright v. Raimondo (2024)
                                      </h5>
                                      <p style={{
                                        fontSize: '12px',
                                        lineHeight: '1.5',
                                        color: '#4b5563'
                                      }}>
                                        {provision.loper_bright_analysis}
                                      </p>
                                    </div>
                                  )}
                                  {provision.sackett_violation && (
                                    <div>
                                      <h5 style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#6b21a8',
                                        marginBottom: '4px'
                                      }}>
                                        Sackett v. EPA (2023)
                                      </h5>
                                      <p style={{
                                        fontSize: '12px',
                                        lineHeight: '1.5',
                                        color: '#4b5563'
                                      }}>
                                        {provision.sackett_analysis}
                                      </p>
                                    </div>
                                  )}
                                  {provision.west_virginia_violation && (
                                    <div>
                                      <h5 style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#6b21a8',
                                        marginBottom: '4px'
                                      }}>
                                        West Virginia v. EPA (2022)
                                      </h5>
                                      <p style={{
                                        fontSize: '12px',
                                        lineHeight: '1.5',
                                        color: '#4b5563'
                                      }}>
                                        {provision.west_virginia_analysis}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </motion.div>
          )}

          {viewMode === 'cba' && (
            <motion.div
              key="cba"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {isLoadingCBA ? (
                <CBAGenerationAnimation />
              ) : cbaGenerated && cbaData ? (
                <CBAComprehensiveDisplay 
                  cbaData={cbaData} 
                  onRegenerateWithContext={async (context) => {
                    console.log('%c🔥 [AnalysisPanel] onRegenerateWithContext TRIGGERED!', 'color: #EF4444; font-weight: bold; font-size: 14px');
                    console.log('%c📝 [AnalysisPanel] User provided context:', 'color: #F59E0B; font-weight: bold', context);
                    console.log('%c🎯 [AnalysisPanel] Current regulation:', 'color: #3B82F6', {
                      id: regulation?.id,
                      title: regulation?.title,
                      hasText: !!regulation?.text
                    });
                    
                    setCbaUserContext(context);
                    console.log('%c🚀 [AnalysisPanel] Calling loadCBA with context...', 'color: #10B981; font-weight: bold');
                    
                    try {
                      await loadCBA(true, context);
                      console.log('%c✅ [AnalysisPanel] CBA regeneration completed!', 'color: #10B981; font-weight: bold; font-size: 14px');
                    } catch (error) {
                      console.error('%c❌ [AnalysisPanel] CBA regeneration FAILED!', 'color: #EF4444; font-weight: bold; font-size: 14px', error);
                      throw error;
                    }
                  }}
                />
              ) : false ? (
                <div style={{ display: 'none' }}>
                  {/* Old display code - keeping temporarily for reference */}
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                      Cost-Benefit Analysis
                    </h3>
                    <p style={{ fontSize: '14px', opacity: 0.9 }}>
                      Analysis timeframe: {cbaData.timeframe} • Discount rate: {cbaData.discount_rate}
                    </p>
                  </div>

                  {/* Summary Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #ffeeee 0%, #ffcdd2 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(239, 68, 68, 0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b' }}>Total Costs</span>
                        <TrendingDown style={{ width: '18px', height: '18px', color: '#dc2626' }} />
                      </div>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#dc2626' }}>
                        ${cbaData.summary.total_costs.toLocaleString()}
                      </p>
                    </div>
                    
                    <div style={{
                      background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(34, 197, 94, 0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#14532d' }}>Total Benefits</span>
                        <TrendingUp style={{ width: '18px', height: '18px', color: '#16a34a' }} />
                      </div>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#16a34a' }}>
                        ${cbaData.summary.total_benefits.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Net Benefit Card */}
                  <div style={{
                    background: cbaData.summary.net_benefit >= 0
                      ? 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)'
                      : 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                    padding: '24px',
                    borderRadius: '12px',
                    border: `1px solid ${cbaData.summary.net_benefit >= 0 ? 'rgba(14, 165, 233, 0.1)' : 'rgba(239, 68, 68, 0.1)'}`,
                    textAlign: 'center'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>Net Benefit</span>
                      <span style={{
                        background: 'white',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: '#2563eb'
                      }}>
                        B/C Ratio: {cbaData.summary.benefit_cost_ratio}
                      </span>
                    </div>
                    <p style={{
                      fontSize: '36px',
                      fontWeight: '700',
                      color: cbaData.summary.net_benefit >= 0 ? '#0ea5e9' : '#ef4444'
                    }}>
                      ${cbaData.summary.net_benefit.toLocaleString()}
                    </p>
                  </div>

                  {/* Detailed Breakdown */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Costs Section */}
                    <div>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#1f2937',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <TrendingDown style={{ width: '18px', height: '18px', color: '#dc2626' }} />
                        Cost Breakdown
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {cbaData.costs.map((cost, index) => {
                          const isExpanded = expandedCosts.includes(index);
                          return (
                            <div key={index} style={{
                              background: 'white',
                              padding: '16px',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              transition: 'all 0.2s ease',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setExpandedCosts(prev =>
                                prev.includes(index)
                                  ? prev.filter(i => i !== index)
                                  : [...prev, index]
                              );
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {isExpanded ? (
                                      <ChevronDown style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                    ) : (
                                      <ChevronRight style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                    )}
                                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                      {cost.category}
                                    </p>
                                  </div>
                                  {!isExpanded && (
                                    <p style={{ 
                                      fontSize: '12px', 
                                      color: '#6b7280', 
                                      lineHeight: '1.4',
                                      marginLeft: '22px',
                                      marginTop: '4px',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}>
                                      {cost.description}
                                    </p>
                                  )}
                                </div>
                                <span style={{
                                  background: '#fee2e2',
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  color: '#dc2626',
                                  whiteSpace: 'nowrap'
                                }}>
                                  ${cost.amount.toLocaleString()}
                                </span>
                              </div>
                              
                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    style={{
                                      marginTop: '12px',
                                      paddingTop: '12px',
                                      borderTop: '1px solid #f3f4f6',
                                      marginLeft: '22px'
                                    }}
                                  >
                                    <div style={{
                                      background: '#f9fafb',
                                      padding: '12px',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      lineHeight: '1.6',
                                      color: '#374151'
                                    }}>
                                      <p style={{ fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
                                        Methodology & Calculation:
                                      </p>
                                      <p>{cost.description}</p>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Benefits Section */}
                    <div>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#1f2937',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <TrendingUp style={{ width: '18px', height: '18px', color: '#16a34a' }} />
                        Benefit Breakdown
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {cbaData.benefits.map((benefit, index) => {
                          const isExpanded = expandedBenefits.includes(index);
                          return (
                            <div key={index} style={{
                              background: 'white',
                              padding: '16px',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              transition: 'all 0.2s ease',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setExpandedBenefits(prev =>
                                prev.includes(index)
                                  ? prev.filter(i => i !== index)
                                  : [...prev, index]
                              );
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {isExpanded ? (
                                      <ChevronDown style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                    ) : (
                                      <ChevronRight style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                    )}
                                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                      {benefit.category}
                                    </p>
                                  </div>
                                  {!isExpanded && (
                                    <p style={{ 
                                      fontSize: '12px', 
                                      color: '#6b7280', 
                                      lineHeight: '1.4',
                                      marginLeft: '22px',
                                      marginTop: '4px',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}>
                                      {benefit.description}
                                    </p>
                                  )}
                                </div>
                                <span style={{
                                  background: '#dcfce7',
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  color: '#16a34a',
                                  whiteSpace: 'nowrap'
                                }}>
                                  ${benefit.amount.toLocaleString()}
                                </span>
                              </div>
                              
                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    style={{
                                      marginTop: '12px',
                                      paddingTop: '12px',
                                      borderTop: '1px solid #f3f4f6',
                                      marginLeft: '22px'
                                    }}
                                  >
                                    <div style={{
                                      background: '#f9fafb',
                                      padding: '12px',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      lineHeight: '1.6',
                                      color: '#374151'
                                    }}>
                                      <p style={{ fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
                                        Methodology & Calculation:
                                      </p>
                                      <p>{benefit.description}</p>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* User Context Input - Pinned to Bottom */}
                  <div style={{
                    position: 'sticky',
                    bottom: 0,
                    background: 'white',
                    borderTop: '1px solid #e5e7eb',
                    padding: '16px',
                    marginTop: '24px',
                    borderRadius: '0 0 12px 12px',
                    boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.05)'
                  }}>
                    {!showCBAInput ? (
                      <button
                        onClick={() => setShowCBAInput(true)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <RefreshCw style={{ width: '16px', height: '16px' }} />
                        Regenerate with Additional Context
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '8px'
                        }}>
                          <MessageSquare style={{ width: '18px', height: '18px', color: '#667eea' }} />
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                            Add Context for CBA Regeneration
                          </span>
                        </div>
                        <textarea
                          value={cbaUserContext}
                          onChange={(e) => setCbaUserContext(e.target.value)}
                          placeholder="Provide additional context, assumptions, or specific factors to consider in the cost-benefit analysis..."
                          style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif',
                            resize: 'vertical',
                            outline: 'none'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#667eea';
                            e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handleRegenerateCBA}
                            disabled={!cbaUserContext.trim() || isLoadingCBA}
                            style={{
                              flex: 1,
                              padding: '10px',
                              background: cbaUserContext.trim() && !isLoadingCBA
                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : '#e5e7eb',
                              color: cbaUserContext.trim() && !isLoadingCBA ? 'white' : '#9ca3af',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: cbaUserContext.trim() && !isLoadingCBA ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <RefreshCw style={{ width: '14px', height: '14px' }} />
                            {isLoadingCBA ? 'Generating...' : 'Regenerate CBA'}
                          </button>
                          <button
                            onClick={() => {
                              setShowCBAInput(false);
                              setCbaUserContext('');
                            }}
                            style={{
                              padding: '10px 16px',
                              background: 'white',
                              color: '#6b7280',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '400px',
                  gap: '24px',
                  padding: '40px'
                }}>
                  <DollarSign style={{ width: '64px', height: '64px', color: '#9ca3af' }} />
                  <div style={{ textAlign: 'center' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
                      Cost-Benefit Analysis
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', maxWidth: '400px' }}>
                      Generate a comprehensive cost-benefit analysis for this regulation to understand its economic impact and implementation requirements.
                    </p>
                  </div>
                  <button
                    onClick={() => loadCBA(false)}
                    disabled={isLoadingCBA}
                    style={{
                      padding: '14px 32px',
                      background: isLoadingCBA ? '#e5e7eb' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: isLoadingCBA ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: isLoadingCBA ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.3s ease',
                      boxShadow: isLoadingCBA ? 'none' : '0 4px 14px rgba(102, 126, 234, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoadingCBA) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoadingCBA) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(102, 126, 234, 0.4)';
                      }
                    }}
                  >
                    <DollarSign style={{ width: '20px', height: '20px' }} />
                    {isLoadingCBA ? 'Generating Analysis...' : 'Generate Cost-Benefit Analysis'}
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {viewMode === 'rewrite' && (
            (() => {
              console.log('%c🎨 [AnalysisPanel] Rendering REWRITE view!', 'color: #10B981; font-weight: bold; font-size: 14px', {
                hasRewriteData: !!rewriteData,
                isLoadingRewrite,
                rewriteError
              });
              return (
                <motion.div
                  key="rewrite"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="h-full"
                >
              {isLoadingRewrite ? (
                <RewriteGenerationAnimation />
              ) : !rewriteData ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  padding: '40px',
                  textAlign: 'center'
                }}>
                  <FileEdit style={{ width: '48px', height: '48px', color: '#9ca3af', marginBottom: '24px' }} />
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '12px',
                    fontFamily: 'Playfair Display, serif'
                  }}>
                    Simplify This Regulation
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '24px',
                    maxWidth: '500px',
                    lineHeight: '1.6'
                  }}>
                    Generate a plain language version that removes redundancies, eliminates unauthorized provisions, 
                    and maintains essential requirements in clear, concise language.
                  </p>
                  <button
                    onClick={() => loadRewrite()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 14px rgba(102, 126, 234, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(102, 126, 234, 0.3)';
                    }}
                  >
                    <FileEdit style={{ width: '20px', height: '20px' }} />
                    Generate Plain Language Rewrite
                  </button>
                </div>
              ) : (
                <RewriteDisplay 
                  rewriteData={rewriteData}
                  isLoading={isLoadingRewrite}
                  error={rewriteError}
                  onRefineWithContext={handleRefineRewrite}
                />
              )}
                </motion.div>
              );
            })()
          )}

          {viewMode === 'bulk-cba' && (
            <motion.div
              key="bulk-cba"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="h-full"
            >
              {isLoadingBulkCBA ? (
                <CBAGenerationAnimation />
              ) : bulkCBAHistory.length > 0 && bulkCBAData ? (
                <div style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
                  <VersionHistory
                    history={bulkCBAHistory}
                    currentVersion={currentBulkCBAVersion}
                    onVersionChange={(version) => {
                      setCurrentBulkCBAVersion(version);
                      setBulkCBAData(bulkCBAHistory[version].data);
                    }}
                    label="Analysis"
                  />
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    marginBottom: '24px'
                  }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                      Bulk Cost-Benefit Analysis
                    </h3>
                    <p style={{ fontSize: '14px', opacity: 0.9 }}>
                      Analyzing {bulkCBAData?.regulations?.length || regulations.length} regulations as a unified package
                    </p>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(239, 68, 68, 0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b' }}>Total Costs</span>
                        <TrendingDown style={{ width: '18px', height: '18px', color: '#dc2626' }} />
                      </div>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#dc2626' }}>
                        ${bulkCBAData?.summary?.totalCosts?.toLocaleString() || '0'}
                      </p>
                    </div>
                    
                    <div style={{
                      background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(34, 197, 94, 0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#14532d' }}>Total Benefits</span>
                        <TrendingUp style={{ width: '18px', height: '18px', color: '#16a34a' }} />
                      </div>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#16a34a' }}>
                        ${bulkCBAData?.summary?.totalBenefits?.toLocaleString() || '0'}
                      </p>
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f9fafb',
                    padding: '20px',
                    borderRadius: '12px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.8',
                    color: '#374151'
                  }}>
                    {bulkCBAData?.analysisText || 'No analysis data available'}
                  </div>
                  
                  {/* Refine Section */}
                  <div style={{
                    marginTop: '32px',
                    paddingTop: '24px',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    {!showBulkCBARefine ? (
                      <button
                        onClick={() => setShowBulkCBARefine(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 20px',
                          background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          width: '100%'
                        }}
                      >
                        <RefreshCw style={{ width: '16px', height: '16px' }} />
                        Refine This Analysis
                      </button>
                    ) : (
                      <div style={{
                        padding: '20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                          Add specific context or requirements to regenerate with more targeted analysis
                        </p>
                        <textarea
                          value={bulkCBARefineContext}
                          onChange={(e) => setBulkCBARefineContext(e.target.value)}
                          placeholder="E.g., Focus on small business impacts, emphasize environmental benefits, include 5-year projections..."
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif',
                            resize: 'vertical',
                            minHeight: '100px'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <button
                            onClick={() => {
                              loadBulkCBA(bulkCBARefineContext);
                              setShowBulkCBARefine(false);
                              setBulkCBARefineContext('');
                            }}
                            disabled={!bulkCBARefineContext.trim()}
                            style={{
                              flex: 1,
                              padding: '10px 20px',
                              background: bulkCBARefineContext.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e5e7eb',
                              color: bulkCBARefineContext.trim() ? 'white' : '#9ca3af',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: bulkCBARefineContext.trim() ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <RefreshCw style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                            Regenerate
                          </button>
                          <button
                            onClick={() => {
                              setShowBulkCBARefine(false);
                              setBulkCBARefineContext('');
                            }}
                            style={{
                              padding: '10px 16px',
                              background: 'white',
                              color: '#6b7280',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  padding: '40px',
                  textAlign: 'center'
                }}>
                  <DollarSign style={{ width: '48px', height: '48px', color: '#9ca3af', marginBottom: '24px' }} />
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '12px',
                    fontFamily: 'Playfair Display, serif'
                  }}>
                    Bulk Cost-Benefit Analysis
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '24px',
                    maxWidth: '500px',
                    lineHeight: '1.6'
                  }}>
                    Generate a comprehensive cost-benefit analysis for all {regulations?.length || 0} selected regulations
                    as a unified regulatory package, identifying synergies and cumulative impacts.
                  </p>
                  <button
                    onClick={() => loadBulkCBA()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 14px rgba(102, 126, 234, 0.3)'
                    }}
                  >
                    <DollarSign style={{ width: '20px', height: '20px' }} />
                    Generate Bulk Analysis
                  </button>
                </div>
              )}
            </motion.div>
          )}
          
          {viewMode === 'consolidate' && (
            <motion.div
              key="consolidate"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="h-full"
            >
              {isLoadingConsolidation ? (
                <RewriteGenerationAnimation />
              ) : consolidationHistory.length > 0 && consolidationData ? (
                <div style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
                  <VersionHistory
                    history={consolidationHistory}
                    currentVersion={currentConsolidationVersion}
                    onVersionChange={(version) => {
                      setCurrentConsolidationVersion(version);
                      setConsolidationData(consolidationHistory[version].data);
                    }}
                    label="Draft"
                  />
                  <div style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    marginBottom: '24px'
                  }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                      Consolidated Regulation
                    </h3>
                    <p style={{ fontSize: '14px', opacity: 0.9 }}>
                      Combined {consolidationData?.regulationCount} regulations into one unified text
                    </p>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                        {consolidationData.originalWords?.toLocaleString()}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>Original Words</p>
                    </div>
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                        {consolidationData.consolidatedWords?.toLocaleString()}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>Consolidated Words</p>
                    </div>
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>
                        {consolidationData.percentReduction}%
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>Reduction</p>
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f9fafb',
                    padding: '20px',
                    borderRadius: '12px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Times New Roman, serif',
                    fontSize: '14px',
                    lineHeight: '1.8',
                    color: '#374151'
                  }}>
                    {consolidationData.consolidatedText}
                  </div>
                  
                  {/* Refine Section */}
                  <div style={{
                    marginTop: '32px',
                    paddingTop: '24px',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    {!showConsolidationRefine ? (
                      <button
                        onClick={() => setShowConsolidationRefine(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 20px',
                          background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          width: '100%'
                        }}
                      >
                        <RefreshCw style={{ width: '16px', height: '16px' }} />
                        Refine This Draft
                      </button>
                    ) : (
                      <div style={{
                        padding: '20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                          Add specific context or requirements to regenerate with more targeted consolidation
                        </p>
                        <textarea
                          value={consolidationRefineContext}
                          onChange={(e) => setConsolidationRefineContext(e.target.value)}
                          placeholder="E.g., Prioritize enforcement provisions, emphasize reporting requirements, remove procedural details..."
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif',
                            resize: 'vertical',
                            minHeight: '100px'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <button
                            onClick={() => {
                              loadConsolidation(consolidationRefineContext);
                              setShowConsolidationRefine(false);
                              setConsolidationRefineContext('');
                            }}
                            disabled={!consolidationRefineContext.trim()}
                            style={{
                              flex: 1,
                              padding: '10px 20px',
                              background: consolidationRefineContext.trim() ? 'linear-gradient(135deg, #3b82f6, #6366f1)' : '#e5e7eb',
                              color: consolidationRefineContext.trim() ? 'white' : '#9ca3af',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: consolidationRefineContext.trim() ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <RefreshCw style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                            Regenerate
                          </button>
                          <button
                            onClick={() => {
                              setShowConsolidationRefine(false);
                              setConsolidationRefineContext('');
                            }}
                            style={{
                              padding: '10px 16px',
                              background: 'white',
                              color: '#6b7280',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  padding: '40px',
                  textAlign: 'center'
                }}>
                  <Layers style={{ width: '48px', height: '48px', color: '#9ca3af', marginBottom: '24px' }} />
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '12px',
                    fontFamily: 'Playfair Display, serif'
                  }}>
                    Consolidate Regulations
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '24px',
                    maxWidth: '500px',
                    lineHeight: '1.6'
                  }}>
                    Combine all {regulations?.length || 0} selected regulations into one succinct regulation,
                    minimizing redundancy while preserving all essential information.
                  </p>
                  <button
                    onClick={() => loadConsolidation()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    <Layers style={{ width: '20px', height: '20px' }} />
                    Generate Consolidated Draft
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {viewMode === 'consolidated' && (
            <motion.div
              key="consolidated"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="h-full"
            >
              <ConsolidatedEditor 
                draft={consolidatedDraft}
                isLoading={isConsolidating}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </>
      )}
    </div>
  );
};

export default AnalysisPanel;